import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Auth, Hub } from "aws-amplify";
import useRefreshPBXToken from "./useRefreshPBXToken.jsx";

const TWO_MIN = 2 * 60 * 1000;
const LOG = true;
const dbg = (...a) => LOG && console.log(...a);
const emit = (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail }));

const pad2 = (n) => String(n).padStart(2, "0");
const toMs = (v) => (v && Number(v) < 1_000_000_000_000 ? Number(v) * 1000 : Number(v) || 0);
const secs = (ms) => (ms ? Math.max(1, Math.ceil((ms - Date.now()) / 1000)) : 0);

function Chip({ label, secondsLeft, tint = "green" }) {
  const mm = pad2(Math.floor(secondsLeft / 60));
  const ss = pad2(secondsLeft % 60);
  let fg = "#2e7d32", bg = "#e8f5e9";
  if (tint === "blue") { fg = "#1565c0"; bg = "#e3f2fd"; }
  if (secondsLeft <= 300 && secondsLeft > 60) { bg = "#fff8e1"; fg = "#ef6c00"; }
  else if (secondsLeft <= 60) { bg = "#ffebee"; fg = "#c62828"; }
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 12, background: bg, color: fg,
      fontWeight: 700, fontSize: 12.5, lineHeight: 1, border: `1px solid ${fg}22`,
      width: "fit-content", fontVariantNumeric: "tabular-nums",
      boxShadow: "0 2px 10px rgba(0,0,0,.12)"
    }}>
      <span style={{ opacity: 0.85 }}>{label}</span>
      <span>{mm}:{ss}</span>
    </div>
  );
}

function Row({ title, tint, tokenLeft, autoLeft }) {
  if (!tokenLeft && !autoLeft) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <Chip label={`${title} TOKEN`} secondsLeft={tokenLeft} tint={tint} />
      <Chip label="AUTO -2m" secondsLeft={autoLeft} tint={tint} />
    </div>
  );
}

export default function TokenRefresher() {
  // ===== PBX =====
  useRefreshPBXToken();
  const pbx = useSelector((s) => s.pbx.value);
  const pbxExp = useMemo(() => toMs(pbx?.token_expire_time), [pbx?.token_expire_time]);
  const [pbxTokenLeft, setPbxTokenLeft] = useState(0);
  const [pbxAutoLeft,  setPbxAutoLeft]  = useState(0);

  useEffect(() => {
    if (!pbx?.token || !pbxExp) { setPbxTokenLeft(0); setPbxAutoLeft(0); return; }
    const tick = () => {
      setPbxTokenLeft(secs(pbxExp));
      setPbxAutoLeft(secs(pbxExp - TWO_MIN));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [pbx?.token, pbxExp]);

  // ===== AWS — solo después de signIn (evento Hub) =====
  const [awsActive, setAwsActive] = useState(false);
  const [awsReady,  setAwsReady]  = useState(false);
  const [awsTokenLeft, setAwsTokenLeft] = useState(0);
  const [awsAutoLeft,  setAwsAutoLeft]  = useState(0);
  const awsExpRef = useRef(0);
  const awsTimer  = useRef(null);
  const tickId    = useRef(null);

  const readAwsExpiryOnce = useCallback(async () => {
    const s = await Auth.currentSession();
    const idt = s.getIdToken();
    const expSec = idt.getExpiration?.() ?? idt.payload?.exp;
    awsExpRef.current = Number(expSec) * 1000;
    dbg("[AWS] Expiración leída", { expISO: new Date(awsExpRef.current).toISOString() });
    return awsExpRef.current;
  }, []);

  const startAwsTicks = useCallback(() => {
    if (tickId.current) clearInterval(tickId.current);
    const tick = () => {
      const exp = awsExpRef.current;
      setAwsTokenLeft(secs(exp));
      setAwsAutoLeft(secs(exp - TWO_MIN));
    };
    tick();
    tickId.current = setInterval(tick, 1000);
  }, []);

  const scheduleAwsRefresh = useCallback(() => {
    if (awsTimer.current) clearTimeout(awsTimer.current);
    const fireIn = Math.max(awsExpRef.current - Date.now() - TWO_MIN, 0);
    const fireAt = Date.now() + fireIn;
    dbg("[AWS] Auto-refresh programado", {
      inSec: Math.ceil(fireIn / 1000),
      at: new Date(fireAt).toISOString(),
    });

    awsTimer.current = setTimeout(async () => {
      try {
        dbg("[AWS] Ejecutando auto-refresh…");
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        const current = await Auth.currentSession();
        const rt = current.getRefreshToken();
        const newSession = await new Promise((res, rej) =>
          user.refreshSession(rt, (err, sess) => (err ? rej(err) : res(sess)))
        );
        const idt = newSession.getIdToken();
        const jwt = idt.getJwtToken();
        sessionStorage.setItem("token", jwt);

        const newExp = (idt.getExpiration?.() ?? idt.payload?.exp) * 1000;
        awsExpRef.current = newExp;

        dbg("[AWS] Token refrescado ✔", { expISO: new Date(newExp).toISOString() });
        emit("aws:tokenRefreshed", { idToken_expire_time: newExp });

        scheduleAwsRefresh(); // reprograma siguiente ciclo
      } catch (e) {
        console.error("[AWS] refresh error:", e);
        emit("aws:tokenRefreshError", { message: String(e) });
      }
    }, fireIn);
  }, []);

  // Activación solo cuando sucede signIn en esta carga
  useEffect(() => {
    const unsub = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signIn") setAwsActive(true);
      if (payload.event === "signOut") {
        setAwsActive(false);
        setAwsReady(false);
        awsExpRef.current = 0;
        setAwsTokenLeft(0);
        setAwsAutoLeft(0);
        if (tickId.current) clearInterval(tickId.current);
        if (awsTimer.current) clearTimeout(awsTimer.current);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!awsActive) return;
      try {
        const exp = await readAwsExpiryOnce();
        if (!mounted || !exp) return;
        setAwsReady(true);
        startAwsTicks();
        scheduleAwsRefresh();
      } catch {
        setAwsReady(false);
      }
    })();
    return () => { mounted = false; };
  }, [awsActive, readAwsExpiryOnce, startAwsTicks, scheduleAwsRefresh]);

  const showPBX = Boolean(pbx?.token) && pbxTokenLeft > 0 && pbxAutoLeft > 0;
  const showAWS = awsActive && awsReady && awsTokenLeft > 0 && awsAutoLeft > 0;
  if (!showPBX && !showAWS) return null;

  const panel = (
    <div
      style={{
        position: "fixed",
        top: 8,
        left: 12,
        zIndex: 999999999,
        isolation: "isolate",
        pointerEvents: "none",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6, pointerEvents: "auto" }}>
        {showPBX && <Row title="PBX" tint="green" tokenLeft={pbxTokenLeft} autoLeft={pbxAutoLeft} />}
        {showAWS && <Row title="AWS" tint="blue"  tokenLeft={awsTokenLeft} autoLeft={awsAutoLeft} />}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
